// CardRevealOverlay — full-screen "you got a card!" moment.
//
// Mounted by KidModeShell whenever useKidCardGrants.pendingGrants has
// anything in it. Props: { grant, onCollect }. The component runs a
// staged CSS-keyframe animation (no framer-motion — matches the
// kids-mode convention of CSS-only animation):
//
//   FLASH   (0.0 – 0.7s) background pulses black ↔ card tint 3x with
//                        a giant "?" centered. Builds suspense.
//   SPIN    (0.7 – 2.5s) card-shaped element rotates rotateY 0 →
//                        1440deg with cubic-bezier deceleration.
//                        Card-back ("?") on the back face, real card
//                        on the front face (we land on rotateY=0).
//   REVEAL  (2.5 – 3.0s) card holds at rotateY=0. Background fades
//                        to a radial glow in the card's tint. Card
//                        face's emoji + label + ×count visible.
//   COLLECT (idle)       subtle wobble + pulsing TAP TO COLLECT CTA
//                        at the bottom.
//   COLLECT tap          card shrinks/translates toward the bottom
//                        (where KidCardDeck lives), then onCollect()
//                        fires at animation end so the parent can
//                        dequeue.
//
// Haptic taps at flash start, at reveal, and at collect — no-op on
// web. Background-then-foreground mid-reveal: we don't try to recover
// where we were; re-mounting on next foreground simply replays from
// FLASH which is the desired "show me my card again" UX.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**) is
// the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { FONT } from "../../lib/tokens";
import { KID_CARD_BY_KEY } from "./cardTypes";
import { playKidSfx } from "../../lib/kidSfx";

const FLASH_MS   = 700;
const SPIN_MS    = 1800;
const FLY_MS     = 360;

async function hapticImpact(weight) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const style = weight === "heavy" ? ImpactStyle.Heavy
                : weight === "medium" ? ImpactStyle.Medium
                : ImpactStyle.Light;
    Haptics.impact({ style }).catch(() => {});
  } catch { /* haptics unavailable */ }
}

export default function CardRevealOverlay({ grant, onCollect }) {
  const card = KID_CARD_BY_KEY[grant?.card_type];
  // Stages: 0 flash, 1 spin, 2 revealed/idle, 3 collecting (flying out)
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!card) return undefined;
    hapticImpact("heavy");
    // Sparkle at the moment the flash starts so the audible cue
    // syncs with the screen flash.
    playKidSfx("sparkle");
    const t1 = setTimeout(() => { setStage(1); }, FLASH_MS);
    const t2 = setTimeout(() => {
      setStage(2);
      hapticImpact("medium");
      // Second sparkle when the card LANDS face-up — reinforces the
      // reveal moment with a sonic exclamation point.
      playKidSfx("sparkle");
    }, FLASH_MS + SPIN_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [card]);

  if (!card) return null;

  const handleCollect = () => {
    if (stage < 2) return;            // ignore taps before the reveal
    if (stage === 3) return;          // already flying
    hapticImpact("light");
    playKidSfx("coin");
    setStage(3);
    setTimeout(() => { onCollect?.(); }, FLY_MS);
  };

  const tint = card.tint;
  const countLabel = grant.count > 1 ? `×${grant.count}` : "";

  // Background per-stage. FLASH uses the keyframe animation directly;
  // REVEAL fades to a radial glow.
  const backgroundForStage = (() => {
    if (stage === 0) return "#000"; // keyframe overrides via animation
    if (stage === 1) return "#0a0a0a";
    // Reveal / collect: soft radial in card tint.
    return `radial-gradient(ellipse at center, ${tint}55 0%, #0a0a0a 70%)`;
  })();

  return (
    <div
      role="dialog"
      aria-label={`You got a ${card.label} card`}
      onClick={handleCollect}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: backgroundForStage,
        animation: stage === 0
          ? `kid-reveal-flash ${FLASH_MS}ms steps(6, end) 1`
          : undefined,
        animationFillMode: "forwards",
        // CSS custom prop lets the keyframe color-pulse hit the tint
        // without a per-card stylesheet.
        "--kid-reveal-tint": tint,
        transition: stage >= 2 ? "background 600ms ease" : "none",
        touchAction: "manipulation",
        cursor: stage >= 2 ? "pointer" : "default",
        overflow: "hidden",
      }}
    >
      {/* The card itself. 3D-perspective wrapper outside so the
          rotation reads as a real flip, not a CSS skew. */}
      <div
        style={{
          perspective: 1600,
          width: 280,
          height: 400,
          maxWidth: "78vw",
          maxHeight: "60vh",
          isolation: "isolate",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
            WebkitTransformStyle: "preserve-3d",
            animation: stage === 0
              ? "kid-reveal-pre 700ms ease-out 1"
              : stage === 1
                ? `kid-reveal-spin ${SPIN_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1) 1 forwards`
                : stage === 2
                  ? "kid-reveal-wobble 2.4s ease-in-out infinite"
                  : "kid-reveal-fly 360ms cubic-bezier(0.34, 1.56, 0.64, 1) 1 forwards",
            transformOrigin: "center center",
            willChange: "transform",
          }}
        >
          {/* FRONT (real card) — visible at rotateY = 0 / 1440. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              borderRadius: 28,
              background: `linear-gradient(160deg, ${tint} 0%, ${tint}cc 100%)`,
              border: `4px solid ${tint}`,
              boxShadow: stage >= 2
                ? `0 0 80px ${tint}, 0 0 32px ${tint}88, 0 20px 50px rgba(0,0,0,0.5)`
                : `0 20px 50px rgba(0,0,0,0.5)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              transition: "box-shadow 600ms ease",
              overflow: "hidden",
            }}
          >
            {/* Glow halo behind the emoji */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 260,
                height: 260,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.18)",
                filter: "blur(40px)",
              }}
            />
            <span
              style={{
                fontSize: 140,
                lineHeight: 1,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.35))",
                position: "relative",
              }}
            >
              {card.emoji}
            </span>
            <span
              style={{
                marginTop: 18,
                fontFamily: FONT.itemTitle,
                fontStyle: "italic",
                fontSize: 44,
                color: "#fff",
                letterSpacing: "0.02em",
                textShadow: "0 2px 8px rgba(0,0,0,0.45)",
                position: "relative",
              }}
            >
              {card.label}
            </span>
            {countLabel && (
              <span
                style={{
                  marginTop: 4,
                  fontFamily: FONT.mono,
                  fontSize: 28,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#fff",
                  textShadow: "0 2px 8px rgba(0,0,0,0.45)",
                  position: "relative",
                }}
              >
                {countLabel}
              </span>
            )}
          </div>
          {/* BACK ("?" mystery) — visible during the spin's far side. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              borderRadius: 28,
              background: "linear-gradient(160deg, #1a1a1a 0%, #3a3a3a 100%)",
              border: `4px solid ${tint}88`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 20px 50px rgba(0,0,0,0.5)`,
            }}
          >
            <span
              style={{
                fontSize: 200,
                color: tint,
                fontFamily: FONT.itemTitle,
                fontStyle: "italic",
                textShadow: `0 0 40px ${tint}`,
              }}
            >
              ?
            </span>
          </div>
        </div>
      </div>

      {/* Bottom CTA — pulsing TAP TO COLLECT, only after the reveal. */}
      <div
        style={{
          position: "absolute",
          bottom: "max(48px, calc(env(safe-area-inset-bottom) + 32px))",
          fontFamily: FONT.mono,
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: "0.18em",
          color: "#fff",
          opacity: stage === 2 ? 1 : 0,
          transition: "opacity 400ms ease 200ms",
          animation: stage === 2 ? "kid-reveal-cta-pulse 1.4s ease-in-out infinite" : undefined,
          pointerEvents: "none",
          textShadow: `0 0 20px ${tint}`,
        }}
      >
        TAP TO COLLECT
      </div>

      {/* Suspense "?" overlay during the flash stage. */}
      {stage === 0 && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            fontSize: 240,
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            color: tint,
            opacity: 0.85,
            mixBlendMode: "screen",
            animation: "kid-reveal-q-pulse 240ms ease-in-out 3",
            pointerEvents: "none",
          }}
        >
          ?
        </div>
      )}

      <style>{`
        @keyframes kid-reveal-flash {
          0%   { background: #000; }
          16%  { background: var(--kid-reveal-tint); }
          33%  { background: #000; }
          50%  { background: var(--kid-reveal-tint); }
          66%  { background: #000; }
          83%  { background: var(--kid-reveal-tint); }
          100% { background: #0a0a0a; }
        }
        @keyframes kid-reveal-q-pulse {
          0%, 100% { transform: scale(0.9); opacity: 0.6; }
          50%      { transform: scale(1.15); opacity: 1; }
        }
        @keyframes kid-reveal-pre {
          0%   { transform: scale(0.4) rotateY(0deg); opacity: 0.2; }
          100% { transform: scale(1)   rotateY(0deg); opacity: 1; }
        }
        @keyframes kid-reveal-spin {
          0%   { transform: scale(1) rotateY(0deg); }
          100% { transform: scale(1) rotateY(1440deg); }
        }
        @keyframes kid-reveal-wobble {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50%      { transform: rotate(2deg)  translateY(-6px); }
        }
        @keyframes kid-reveal-fly {
          0%   { transform: translateY(0)     scale(1);   opacity: 1; }
          60%  { transform: translateY(40vh)  scale(0.55); opacity: 1; }
          100% { transform: translateY(60vh)  scale(0.05); opacity: 0; }
        }
        @keyframes kid-reveal-cta-pulse {
          0%, 100% { opacity: 0.7; transform: translateY(0); }
          50%      { opacity: 1;   transform: translateY(-2px); }
        }
      `}</style>
    </div>
  );
}
